import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import Topbar from '../../Topbar';
import { SearchIcon } from '../theme';
import { useTheme } from '../../ThemeContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

// 1. Données de secours (MOCK) réalignées EXACTEMENT sur le format de la documentation
const MOCK_TESTS = [
  {
    id: 1,
    job_name: "Senior Full Stack Developer",
    company_name: "TechCorp Inc.",
    experience_level: "SENIOR",
    test_duration_min: 60,
    skills: [{ name: "React" }, { name: "Node.js" }, { name: "AWS" }],
    description: "Build a scalable cloud application and secure endpoints."
  },
  {
    id: 2,
    job_name: "Frontend Engineer",
    company_name: "Startup.io",
    experience_level: "MID",
    test_duration_min: 45,
    skills: [{ name: "React" }, { name: "TypeScript" }, { name: "Next.js" }],
    description: "Implement pixel-perfect responsive layouts."
  },
  {
    id: 3,
    job_name: "React Developer",
    company_name: "DataSystems GmbH",
    experience_level: "ENTRY",
    test_duration_min: 90,
    skills: [{ name: "React" }, { name: "GraphQL" }],
    description: "Optimize state management and data fetching workflows."
  }
];

const FilterButton = ({ label, active, onClick }) => {
  const { C } = useTheme();
  return (
    <button onClick={onClick} style={{
      padding: "8px 16px", borderRadius: 8,
      background: active ? C.accentDim : C.bgCard,
      border: `1px solid ${active ? C.accent : C.border}`,
      color: active ? C.textPrimary : C.textSecond,
      fontSize: 13, cursor: "pointer", transition: "all 0.2s"
    }}>{label}</button>
  );
};

// Composant de carte mis à jour avec les propriétés de la documentation
const JobCard = ({ test }) => {
  const { C, isDark } = useTheme();
  const navigate = useNavigate();

  // Génération d'initiales et d'une couleur à partir du nom de l'entreprise
  const initials = test.company_name ? test.company_name.substring(0, 2).toUpperCase() : "SH";

  return (
    <div
      onClick={() => navigate(`/dashboard/test/${test.id}`)}
      style={{
        background: isDark ? "rgba(93, 126, 160, 0.23)" : "rgba(221, 205, 248, 0.36)",
        border: `1px solid ${C.border}`, borderRadius: 16, padding: "20px 24px",
        display: "flex", alignItems: "center", gap: 20, marginBottom: 16,
        transition: "transform 0.2s, border-color 0.2s", cursor: "pointer"
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = C.accent;
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = C.border;
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      {/* Badge Entreprise */}
      <div style={{
        width: 54, height: 54, borderRadius: 12,
        background: "#6c63ff", display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 18, fontWeight: 700, color: "#fff"
      }}>
        {initials}
      </div>

      {/* Informations conformes à l'API */}
      <div style={{ flex: 1 }}>
        <h3 style={{ margin: 0, fontSize: 16, color: C.textPrimary }}>{test.job_name}</h3>
        <div style={{ fontSize: 13, color: C.textSecond, marginTop: 4 }}>
          {test.company_name} · <span style={{ fontWeight: 600 }}>{test.experience_level}</span> · {test.test_duration_min} min
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          {test.skills?.map((sk, index) => (
            <span key={index} style={{
              fontSize: 11, padding: "4px 10px", borderRadius: 6,
              background: "rgba(185, 222, 244, 0.22)", color: C.accent,
              border: `1px solid rgba(139, 92, 246, 0.2)`
            }}>
              {sk.name}
            </span>
          ))}
        </div>
      </div>

      {/* Bouton d'action */}
      <div style={{ textAlign: "right" }}>
        <motion.button
          whileHover={{ backgroundColor: "rgba(194, 180, 236, 0.41)", borderColor: C.accent, scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/dashboard/test/${test.id}`);
          }}
          style={{
            background: "rgba(194, 180, 236, 0.33)", border: `1px solid ${C.border}`,
            color: C.textPrimary, padding: "8px 16px", borderRadius: 8,
            fontSize: 13, fontWeight: 600, cursor: "pointer"
          }}
        >
          Start Challenge
        </motion.button>
      </div>
    </div>
  );
};

export default function Jobs() {
  const { C } = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("All");
  const [tests, setTests] = useState(MOCK_TESTS);
  const { onMenuClick } = useOutletContext();
  useEffect(() => {
    const fetchSavedTests = async () => {
      try {
        const response = await fetch('/api/candidates/saved-tests/');
        if (response.ok) {
          const resBody = await response.json();
          if (resBody.status && resBody.data) {
            setTests([...resBody.data, ...MOCK_TESTS]);
          }
        }
      } catch (error) {
        console.log("API SkillHunter hors ligne. Utilisation des données MOCK localisées.");
      }
    };
    fetchSavedTests();
  }, []);

  const filteredTests = tests.filter(test => {
    const matchesSearch = 
      test.job_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      test.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      test.skills?.some(sk => sk.name.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesLevel = selectedLevel === "All" || test.experience_level === selectedLevel;
    return matchesSearch && matchesLevel;
  });

  return (
    <div style={{ height: "100vh", flex: 1, display: "flex", flexDirection: "column", background: C.bgDeep }}>
      <Topbar title="SkillHunter Challenges" onMenuClick={onMenuClick}/>

      <div style={{ flex: 1, overflowY: "auto", padding: "40px 60px" }}>
        {/* Header Section */}
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: C.textPrimary, margin: "0 0 8px 0" }}>Available Tests</h1>
          <p style={{ fontSize: 15, color: C.textSecond, margin: 0 }}>
            Complete corporate challenges, submit your flag and get verified scores.
          </p>
        </div>

        {/* Filter Bar */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 32 }}>
          <div style={{ position: "relative", flex: 1, maxWidth: 300, marginRight: "42px" }}>
            <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}>
              <SearchIcon color="gray" />
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search test, company or skill..."
              style={{
                width: "100%", background: C.bgCard,
                border: `1px solid ${C.border}`, borderRadius: 8,
                color: C.textPrimary, fontSize: 13,
                padding: "10px 14px 10px 36px", fontFamily: "inherit", outline: "none",
                marginBottom: "0" // <-- Remis en place ici !
              }}
            />
          </div>

          <FilterButton 
            label="All Levels" 
            active={selectedLevel === "All"}
            onClick={() => setSelectedLevel("All")}
          />
          <FilterButton 
            label="Only Senior" 
            active={selectedLevel === "SENIOR"}
            onClick={() => setSelectedLevel(prev => prev === "SENIOR" ? "All" : "SENIOR")}
          />
        </div>

        {/* Job List */}
        <div style={{ maxWidth: 1000 }}>
          {filteredTests.length > 0 ? (
            filteredTests.map(test => (
              <JobCard key={test.id} test={test} />
            ))
          ) : (
            <p style={{ color: C.textSecond, fontSize: 14, textAlign: "center", marginTop: 40 }}>
              No tests matching your criteria.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}